<script setup lang="ts">
import Bell from '@/assets/icons/bell.svg'
import Map from '@/assets/icons/map.svg'
import Bookmark from '@/assets/icons/bookmark.svg'
import { EnvelopeIcon, ChevronDownIcon } from '@heroicons/vue/24/outline'
import { useToast } from 'vue-toast-notification'
import { ref, watch } from 'vue'
import { PreferenceType, useUserPreferenceStore, type UserPreference } from '@/stores/useUserPreferenceStore'
import { useBoostCorePermissionStore } from '@/stores/useBoostCorePermissionStore'
import SwitchInput from '@/components/common/inputs/SwitchInput.vue'
import ButtonDaisy from '@/components/common/buttons/ButtonDaisy.vue'
import Modal from '@/components/common/layout/Modal.vue'
import ProspectionButtonsPreference from '@/components/features/user/ProspectionButtonsPreference.vue'
import Client360TabsConfigList from '@/components/features/partners/client360/Client360TabsConfigList.vue'
import { normalizeClient360Pref } from '@/components/features/partners/client360/client360Sections'

const props = defineProps<{
  showModal: boolean
}>()

const emit = defineEmits<{
  (e: 'close'): void
}>()

const userPreferenceStore = useUserPreferenceStore()
const boostCoreStore = useBoostCorePermissionStore()
const toast = useToast()

const closeModalPreferences = () => {
  emit('close')
}

const savePreference = async (pref: UserPreference) => {
  const success = await userPreferenceStore.updatePreference(pref.code, pref.data, true)
  if (success) {
    toast.success('Préférences sauvegardées.')
  } else {
    toast.error('Une erreur est survenue lors de la sauvegarde.')
  }
}

const addButton = (pref: UserPreference) => {
  if (!pref.data.buttons) pref.data.buttons = []
  if (pref.data.buttons.length < 5) {
    pref.data.buttons.push({ label: '', text: '', color: '#3b82f6' })
  }
}

const removeButton = (pref: UserPreference, index: number) => {
  pref.data.buttons.splice(index, 1)
}

// Fiche client 360° : ordre + visibilité des sections (stockés sous PAGE.data.client360)
const onClient360Change = (pref: UserPreference, value: { order: string[]; hidden: string[] }) => {
  if (!pref.data || typeof pref.data !== 'object') pref.data = {}
  pref.data.client360 = { order: value.order, hidden: value.hidden }
  savePreference(pref)
}

// Initialise les clés manquantes de MAIL avec les valeurs par défaut
watch(
  () => userPreferenceStore.preferences,
  (prefs) => {
    const mailPref = prefs.find(p => p.code === PreferenceType.MAIL)
    if (mailPref?.data && !mailPref.data.derogations) {
      mailPref.data.derogations = { toValidate: true, decision: true }
    }
  },
  { immediate: true },
)

const openSections = ref<Record<string, boolean>>({
  [PreferenceType.MAIL]: true,
  [PreferenceType.NOTIFICATION]: true,
  [PreferenceType.PAGE]: true,
  [PreferenceType.PROSPECTION_BUTTONS]: true,
})

const toggleSection = (code: string) => {
  openSections.value[code] = !openSections.value[code]
}

// État d'ouverture du collapse "Fiche client 360°" dans les préférences PAGE.
const client360Open = ref(true)
</script>

<template>
  <Modal :show-modal="props.showModal" help-key="user-preference" title="Mes Préférences" @close="closeModalPreferences">
    <template #content>
      <div class="mt-4 flex flex-col gap-4">
        <div v-if="userPreferenceStore.loading" class="flex justify-center p-4">
          <span class="loading loading-spinner loading-md"></span>
        </div>
        <div v-else-if="userPreferenceStore.preferences.length > 0" class="overflow-y-auto max-h-[60vh] space-y-2 pr-1">
          <div v-for="pref in userPreferenceStore.preferences" :key="pref.code" class="border border-base-200 rounded-lg">
            <button
              type="button"
              class="flex items-center justify-between w-full px-4 py-3 text-left hover:bg-base-200/50 transition-colors rounded-lg"
              @click="toggleSection(pref.code)"
            >
              <h3 class="font-bold text-base flex items-center gap-2">
                <template v-if="pref.code === PreferenceType.MAIL">
                  <EnvelopeIcon class="w-5 h-5" />
                  Préférences Emails
                </template>
                <template v-else-if="pref.code === PreferenceType.NOTIFICATION">
                  <Bell class="w-5 h-5" />
                  Notifications
                </template>
                <template v-else-if="pref.code === PreferenceType.PAGE">
                  <Map class="w-5 h-5" />
                  Affichage des pages
                </template>
                <template v-else-if="pref.code === PreferenceType.PROSPECTION_BUTTONS">
                  <Bookmark class="w-5 h-5" />
                  Raccourcis Prospection
                </template>
                <span v-else>{{ pref.code }}</span>
              </h3>
              <ChevronDownIcon
                class="w-4 h-4 shrink-0 transition-transform duration-200"
                :class="{ 'rotate-180': openSections[pref.code] }"
              />
            </button>

            <div v-show="openSections[pref.code]" class="px-4 pb-4">
              <div v-if="pref.code === PreferenceType.MAIL" class="space-y-3">
                <template v-if="pref.data?.orders">
                  <p class="text-xs font-semibold text-base-content/50 uppercase tracking-wide">Commandes</p>
                  <SwitchInput
                    label="Mes commandes"
                    v-model="pref.data.orders.direct"
                    @update:model-value="savePreference(pref)"
                  />
                  <SwitchInput
                    label="Commandes de mes commerciaux"
                    v-model="pref.data.orders.manager"
                    @update:model-value="savePreference(pref)"
                  />
                </template>
                <template v-if="pref.data?.derogations">
                  <p class="text-xs font-semibold text-base-content/50 uppercase tracking-wide pt-1">Dérogations</p>
                  <SwitchInput
                    v-if="boostCoreStore.isManager"
                    label="Recevoir un email quand une dérogation est à valider"
                    v-model="pref.data.derogations.toValidate"
                    @update:model-value="savePreference(pref)"
                  />
                  <SwitchInput
                    label="Recevoir un email quand ma dérogation est approuvée ou refusée"
                    v-model="pref.data.derogations.decision"
                    @update:model-value="savePreference(pref)"
                  />
                </template>
              </div>

              <div v-else-if="pref.code === PreferenceType.NOTIFICATION">
                <SwitchInput
                  v-if="pref.data?.orders"
                  label="Mes commandes"
                  v-model="pref.data.orders.direct"
                  @update:model-value="savePreference(pref)"
                />
                <SwitchInput
                  v-if="pref.data?.orders"
                  label="Commandes de mes commerciaux"
                  v-model="pref.data.orders.manager"
                  @update:model-value="savePreference(pref)"
                />
              </div>

              <div v-else-if="pref.code === PreferenceType.PAGE" class="space-y-2">
                <!-- Un collapse par zone de préférences de page (extensible) -->
                <div class="border border-base-200 rounded-lg bg-base-100 overflow-hidden">
                  <button
                    type="button"
                    class="w-full flex items-center justify-between px-3 py-2 text-sm font-medium hover:bg-base-200/50 transition-colors"
                    @click="client360Open = !client360Open"
                  >
                    <span>Fiche client 360°</span>
                    <ChevronDownIcon class="w-4 h-4 transition-transform" :class="client360Open ? 'rotate-180' : ''" />
                  </button>
                  <div v-show="client360Open" class="px-3 pb-3">
                    <p class="text-xs text-base-content/60 mb-3">
                      Ordre et visibilité des sections (glisser pour réordonner, œil pour masquer).
                    </p>
                    <Client360TabsConfigList
                      :order="normalizeClient360Pref(pref.data?.client360 ?? pref.data).order"
                      :hidden="normalizeClient360Pref(pref.data?.client360 ?? pref.data).hidden"
                      @change="(v) => onClient360Change(pref, v)"
                    />
                  </div>
                </div>
              </div>

              <div v-else-if="pref.code === PreferenceType.PROSPECTION_BUTTONS">
                <ProspectionButtonsPreference
                  :buttons="pref.data?.buttons ?? []"
                  @add="addButton(pref)"
                  @remove="(i) => removeButton(pref, i)"
                  @save="savePreference(pref)"
                />
              </div>
            </div>
          </div>
        </div>
        <div v-else class="text-center p-4">
          <p class="text-base-content/80">Aucune préférence trouvée.</p>
        </div>

        <div class="flex justify-end gap-2">
          <ButtonDaisy text="Fermer" variant="ghost" @click="closeModalPreferences()" />
        </div>
      </div>
    </template>
  </Modal>
</template>
